import React, { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { CheckCircle, XCircle, Tag, Percent } from 'lucide-react'
import useDiscountStore from '@/context/DiscountStore'

const DiscountNotification: React.FC = () => {
    const { discountCode } = useDiscountStore()
    const [showNotification, setShowNotification] = useState(false)
    const [notificationData, setNotificationData] = useState<{
        type: 'success' | 'error'
        message: string
        code: string
    } | null>(null)

    useEffect(() => {
        if (discountCode && !discountCode.isLoading) {
            if (discountCode.valid) {
                let message = ''
                if (discountCode.discountType === 'free_addon') {
                    message = `Free Headband Engraving`
                } else if (discountCode.discountType === 'percentage') {
                    message = `${discountCode.discountValue}% off`
                } else if (discountCode.discountType === 'fixed') {
                    message = `£${discountCode.discountValue} off`
                }

                setNotificationData({
                    type: 'success',
                    message: message,
                    code: discountCode.code
                })
            } else if (discountCode.error) {
                setNotificationData({
                    type: 'error',
                    message: discountCode.error,
                    code: discountCode.code
                })
            }
            setShowNotification(true)

            // Auto hide after 4 seconds
            const timer = setTimeout(() => {
                setShowNotification(false)
            }, 4000)

            return () => clearTimeout(timer)
        }
    }, [discountCode])

    if (!notificationData) return null

    return (
        <AnimatePresence>
            {showNotification && (
                <motion.div
                    initial={{ opacity: 0, y: -100, scale: 0.8 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -100, scale: 0.8 }}
                    transition={{
                        type: "spring",
                        damping: 25,
                        stiffness: 300,
                        duration: 0.6
                    }}
                    className="fixed top-12 left-0 right-0 z-[40] flex justify-center px-4 pointer-events-none"
                >
                    <div className={`
                        p-4 rounded-xl shadow-2xl backdrop-blur-sm max-w-md w-full pointer-events-auto
                        ${notificationData.type === 'success'
                            ? 'bg-green text-white'
                            : 'bg-red-50 border-red-200 text-red-800'
                        }
                    `}>
                        <div className="flex items-center justify-center gap-3">
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: 0.2, type: "spring", damping: 15 }}
                                className={`
                                    p-2 rounded-full
                                    ${notificationData.type === 'success'
                                        ? 'bg-white'
                                        : 'bg-red-100'
                                    }
                                `}
                            >
                                {notificationData.type === 'success' ? (
                                    <div className="relative">
                                        <Tag className="w-5 h-5 text-green" />
                                        {discountCode?.discountType === 'percentage' && (
                                            <Percent className="w-3 h-3 absolute -top-1 -right-1" />
                                        )}
                                    </div>
                                ) : (
                                    <XCircle className="w-5 h-5 text-primary" />
                                )}
                            </motion.div>

                            <div className="flex-1">
                                <motion.div
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.3 }}
                                    className="font-semibold text-sm"
                                >
                                    {notificationData.type === 'success' ? `${discountCode?.label || discountCode?.code || 'Discount'} is Active!` : 'Invalid Code'}
                                </motion.div>
                                <motion.div
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.4 }}
                                    className="text-xs opacity-80"
                                >
                                    {notificationData.message}
                                </motion.div>
                            </div>

                            <motion.button
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.5 }}
                                onClick={() => setShowNotification(false)}
                                className="text-current opacity-50 hover:opacity-100 transition-opacity"
                            >
                                <XCircle className="size-6" />
                            </motion.button>
                        </div>

                        {/* Progress bar for auto-hide */}
                        <motion.div
                            initial={{ width: "100%" }}
                            animate={{ width: "0%" }}
                            transition={{ duration: 4, ease: "linear" }}
                            className={`
                                mt-3 h-1 rounded-full
                                ${notificationData.type === 'success'
                                    ? 'bg-white'
                                    : 'bg-red-300'
                                }
                            `}
                        />
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}

export default DiscountNotification 